import { TypedDocumentNode, gql } from '@apollo/client';
import classNames from 'classnames';
import { FormattedMessage } from 'react-intl';
import { Outlet, useLocation } from 'react-router-dom';
import styled from 'styled-components';

import { Navigate, catchAll, useTypedParams } from '@sorare/routing';

import { Rarity, Sport } from '@sorare/core/src/__generated__/globalTypes';
import { Box as BoxIcon } from '@sorare/core/src/atoms/icons/Box';
import { BottomSheet } from '@sorare/core/src/atoms/layout/BottomSheet';
import { ContentWithStickyHeader } from '@sorare/core/src/atoms/layout/ContentWithStickyHeader';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { Tab, TabBar } from '@sorare/core/src/atoms/navigation/TabBar';
import { Tooltip } from '@sorare/core/src/atoms/tooltip/Tooltip';
import { LabelM } from '@sorare/core/src/atoms/typography';
import { Badge } from '@sorare/core/src/atoms/ui/Badge';
import { NewPageHeader } from '@sorare/core/src/components/navigation/NewPageHeader';
import { ProfilePageHeader } from '@sorare/core/src/components/navigation/ProfilePageHeader';
import {
  ANY_SPORT_MY_CARDS,
  ANY_SPORT_MY_CARDS_BOXES,
  ANY_SPORT_MY_CARDS_BOXES_TYPE,
  ANY_SPORT_MY_CARDS_CARDS,
  ANY_SPORT_MY_CARDS_CARDS_TYPE,
  ANY_SPORT_MY_CARDS_COLLECTIONS,
  ANY_SPORT_MY_CARDS_COLLECTIONS_TYPE,
  ANY_SPORT_MY_CARDS_ESSENCE,
  ANY_SPORT_MY_CARDS_ESSENCE_TYPE,
} from '@sorare/core/src/constants/__generated__/routes';
import { FRONTEND_ASSET_HOST } from '@sorare/core/src/constants/assets';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useIsLaptop } from '@sorare/core/src/hooks/device/useIsLaptop';
import { useFeatureFlags } from '@sorare/core/src/hooks/useFeatureFlags';
import { LIFECYCLE, useLifecycle } from '@sorare/core/src/hooks/useLifecycle';
import { useQueryLoaderResult } from '@sorare/core/src/hooks/useQueryLoaderResult';
import { fantasy, galleryTabs, navLabels } from '@sorare/core/src/lib/glossary';
import { queryLoader } from '@sorare/core/src/lib/navigation/queryLoader';
import { shardsIconUrl } from '@sorare/core/src/lib/rewards';
import { RequireAuth } from '@sorare/core/src/routing/RequireAuth';

import { CardOrCollectionRaritySwitcher } from 'components/myCards/CardOrCollectionRaritySwitcher';
import { MyCardsRaritySwitcherBox } from 'components/myCards/CardOrCollectionRaritySwitcher/ui';
import { NewRewardsBanner } from 'components/rewards/NewRewardsBanner';
import { LastFixtureStory } from 'components/so5/LastFixtureStory';

import { MyCardsLayoutContext } from './MyCardsLayoutContext';
import {
  MyCardsLayoutQuery,
  MyCardsLayoutQueryVariables,
  UnopenedRewardBoxes_CurrentUser,
} from './__generated__/index.graphql';

const RarityWrapper = styled(Horizontal)`
  padding-bottom: var(--double-unit);
`;

const RewardsBannerWrapper = styled.div`
  padding: var(--double-unit);
`;

const BOTTOM_SHEET_RARITY_SWITCHER_HEIGHT = 90;

// use to keep last user selected state on My Cards revisit
let userSelectedState: boolean;

const MyCardsLayoutComponent = ({
  currentUser,
  type,
}: {
  currentUser: MyCardsLayoutQuery['currentUser'];
  type: Nullable<Exclude<Rarity, Rarity.custom_series> | 'rivals'>;
}) => {
  const { generateSportContextPath, matchAnySportPath, sport } =
    useSportContext();
  const {
    flags: { useMlbOffseason = false, useNewNav = false },
  } = useFeatureFlags();
  const isLaptopAndAbove = useIsLaptop();
  const location = useLocation();
  const { lifecycle, update } = useLifecycle();

  const { currentUser: contextCurrentUser } = useCurrentUserContext();
  const {
    unclaimedSo5Rewards = [],
    betaTesterFootballCommonCardsGame,
    unopenedRewardBoxCommon,
    unopenedRewardBoxLimited,
    unopenedRewardBoxRare,
    unopenedRewardBoxSuperRare,
    unopenedRewardBoxUnique,
    unopenedRewardBoxRivals,
    cardShardsChests,
  } = currentUser || {};
  const { cardCounts } = contextCurrentUser || {};

  const unopedBoxCount =
    (unopenedRewardBoxCommon?.totalCount || 0) +
    (unopenedRewardBoxLimited?.totalCount || 0) +
    (unopenedRewardBoxRare?.totalCount || 0) +
    (unopenedRewardBoxSuperRare?.totalCount || 0) +
    (unopenedRewardBoxUnique?.totalCount || 0) +
    (unopenedRewardBoxRivals?.totalCount || 0);

  const cardsCountByRarity: Record<
    Exclude<Rarity, Rarity.custom_series>,
    number
  > = {
    [Rarity.common]: cardCounts?.common || 0,
    [Rarity.limited]: cardCounts?.limited || 0,
    [Rarity.rare]: cardCounts?.rare || 0,
    [Rarity.super_rare]: cardCounts?.superRare || 0,
    [Rarity.unique]: cardCounts?.unique || 0,
  };
  const blockchainRarities = [
    Rarity.common,
    Rarity.limited,
    Rarity.rare,
    Rarity.super_rare,
    Rarity.unique,
  ].filter(
    rarityType =>
      rarityType !== Rarity.common ||
      (betaTesterFootballCommonCardsGame && sport === Sport.FOOTBALL)
  );

  const boxTypes = [
    ...blockchainRarities,
    ...(sport === Sport.FOOTBALL ? ['rivals'] : []),
  ] as const;

  const unopenedProbabilisticBundlesByRarity: Record<
    (typeof boxTypes)[number],
    number
  > = {
    custom_series: 0,
    common: unopenedRewardBoxCommon?.totalCount ?? 0,
    limited: unopenedRewardBoxLimited?.totalCount ?? 0,
    rare: unopenedRewardBoxRare?.totalCount ?? 0,
    super_rare: unopenedRewardBoxSuperRare?.totalCount ?? 0,
    unique: unopenedRewardBoxUnique?.totalCount ?? 0,
    rivals: unopenedRewardBoxRivals?.totalCount ?? 0,
  };

  const hasCardsOfCurrentRarity =
    !!type && type !== 'rivals' && cardsCountByRarity[type] > 0;
  const defaultCardRarity = hasCardsOfCurrentRarity
    ? type
    : Object.entries(cardsCountByRarity).find(([, number]) => !!number)?.[0] ||
      Rarity.limited;

  const hasBoxesOfCurrentRarity =
    !!type && unopenedProbabilisticBundlesByRarity[type] > 0;
  const defaultBoxRarity = hasBoxesOfCurrentRarity
    ? type
    : Object.entries(unopenedProbabilisticBundlesByRarity).find(
        ([, number]) => !!number
      )?.[0] || Rarity.limited;

  const essenceOfCurrentRarity = cardShardsChests?.find(
    chest => chest.rarity === type
  );
  const hasEssenceOfCurrentRarity =
    !!type && !!essenceOfCurrentRarity?.cardShardsCount;
  const defaultEssenceRarity = hasEssenceOfCurrentRarity
    ? type
    : cardShardsChests?.find(chest => chest.cardShardsCount > 0)?.rarity ||
      Rarity.limited;

  const showEssenceTab =
    sport === Sport.FOOTBALL ||
    sport === Sport.NBA ||
    (sport === Sport.BASEBALL && !useMlbOffseason);

  const tabs = (
    <>
      <Tab
        to={generateSportContextPath(ANY_SPORT_MY_CARDS_CARDS_TYPE, {
          params: {
            type: defaultCardRarity,
          },
        })}
        matches={[
          catchAll(generateSportContextPath(ANY_SPORT_MY_CARDS_CARDS)),
          catchAll(generateSportContextPath(ANY_SPORT_MY_CARDS_COLLECTIONS)),
        ]}
      >
        <FormattedMessage {...galleryTabs.cards} />
      </Tab>
      <Tooltip
        skip={isLaptopAndAbove || unopedBoxCount === 0}
        open
        placement="top"
        title={
          <Horizontal gap={0.5}>
            <LabelM bold>{unopedBoxCount}</LabelM>
            <BoxIcon />
          </Horizontal>
        }
        variant="red"
      >
        <Tab
          to={generateSportContextPath(ANY_SPORT_MY_CARDS_BOXES_TYPE, {
            params: { type: defaultBoxRarity },
          })}
          matches={[
            catchAll(generateSportContextPath(ANY_SPORT_MY_CARDS_BOXES)),
          ]}
        >
          <Badge
            invisible={!isLaptopAndAbove || unopedBoxCount === 0}
            badgeContent={unopedBoxCount}
          >
            <FormattedMessage {...fantasy.boxes} />
          </Badge>
        </Tab>
      </Tooltip>
      {showEssenceTab && (
        <Tab
          to={generateSportContextPath(ANY_SPORT_MY_CARDS_ESSENCE_TYPE, {
            params: {
              type: defaultEssenceRarity,
            },
          })}
          matches={[
            catchAll(generateSportContextPath(ANY_SPORT_MY_CARDS_ESSENCE)),
          ]}
        >
          <FormattedMessage {...fantasy.essenceShort} />
        </Tab>
      )}
    </>
  );

  const hasMoreThan1Rarity =
    Object.values(cardsCountByRarity).filter(Boolean).length > 1;

  const matchCollectionsPage = matchAnySportPath(
    catchAll(ANY_SPORT_MY_CARDS_COLLECTIONS),
    location.pathname
  );

  const lastVisitedRarity =
    lifecycle?.[LIFECYCLE.lastVisitedMyCardsRarity]?.[sport];

  const defaultRarity =
    (lastVisitedRarity as Rarity) ||
    (Object.entries(cardsCountByRarity).find(
      ([, value]) => !!value
    )?.[0] as Rarity) ||
    Rarity.limited;

  const updateLastVisitedRarity = (clickedRarity: Rarity) => {
    update(LIFECYCLE.lastVisitedMyCardsRarity, {
      ...(lifecycle?.[LIFECYCLE.lastVisitedMyCardsRarity] || {}),
      [sport]: clickedRarity,
    });
  };

  if (isLaptopAndAbove) {
    return (
      <>
        {useNewNav ? (
          <NewPageHeader text={navLabels.myCards} disableBackBrowser />
        ) : (
          <ProfilePageHeader showCommonCoins />
        )}
        <NewRewardsBanner
          so5Rewards={unclaimedSo5Rewards}
          Wrapper={RewardsBannerWrapper}
        />
        <ContentWithStickyHeader
          header={
            <TabBar fullWidth variant="flat">
              {tabs}
            </TabBar>
          }
        >
          <MyCardsLayoutContext.Provider
            value={{
              defaultRarity,
            }}
          >
            <Outlet />
          </MyCardsLayoutContext.Provider>
        </ContentWithStickyHeader>
      </>
    );
  }

  return (
    <>
      {useNewNav ? (
        <NewPageHeader text={navLabels.myCards} disableBackBrowser />
      ) : (
        <ProfilePageHeader showCommonCoins />
      )}
      <NewRewardsBanner
        so5Rewards={unclaimedSo5Rewards}
        Wrapper={RewardsBannerWrapper}
      />
      <MyCardsLayoutContext.Provider
        value={{
          defaultRarity,
        }}
      >
        <Outlet />
      </MyCardsLayoutContext.Provider>
      <BottomSheet
        snapPoints={[BOTTOM_SHEET_RARITY_SWITCHER_HEIGHT]}
        openInitially={
          typeof userSelectedState !== 'undefined'
            ? userSelectedState
            : hasMoreThan1Rarity
        }
        onChange={(value: boolean) => {
          userSelectedState = value;
        }}
      >
        <Vertical center gap={3}>
          <TabBar variant="primary" compact>
            {tabs}
          </TabBar>
          <RarityWrapper>
            {(matchAnySportPath(
              catchAll(ANY_SPORT_MY_CARDS_CARDS),
              location.pathname
            ) ||
              matchCollectionsPage) &&
              type &&
              type !== 'rivals' && (
                <CardOrCollectionRaritySwitcher
                  user={contextCurrentUser}
                  type={type}
                  rarityPath={newRarity => {
                    if (matchCollectionsPage) {
                      return generateSportContextPath(
                        ANY_SPORT_MY_CARDS_COLLECTIONS_TYPE,
                        {
                          params: {
                            type: newRarity,
                          },
                        }
                      );
                    }
                    return generateSportContextPath(
                      ANY_SPORT_MY_CARDS_CARDS_TYPE,
                      {
                        params: {
                          type: newRarity,
                        },
                      }
                    );
                  }}
                  onClick={updateLastVisitedRarity}
                />
              )}
            {matchAnySportPath(
              catchAll(ANY_SPORT_MY_CARDS_BOXES),
              location.pathname
            ) &&
              boxTypes.map(key => (
                <MyCardsRaritySwitcherBox
                  key={key}
                  to={generateSportContextPath(ANY_SPORT_MY_CARDS_BOXES_TYPE, {
                    params: {
                      type: key,
                    },
                  })}
                  className={classNames({
                    active: type === key,
                  })}
                >
                  <img
                    src={`${FRONTEND_ASSET_HOST}/football/probabilistic_bundles/${
                      key === 'rivals'
                        ? 'rivals-daily-claim-3'
                        : `${key}-standard`
                    }/picture.png`}
                    alt=""
                    width={32}
                    height={32}
                  />
                  <LabelM bold>
                    {unopenedProbabilisticBundlesByRarity[key]}
                  </LabelM>
                </MyCardsRaritySwitcherBox>
              ))}
            {matchAnySportPath(
              catchAll(ANY_SPORT_MY_CARDS_ESSENCE),
              location.pathname
            ) &&
              blockchainRarities.map(key => {
                const shardsChest = cardShardsChests?.find(
                  chest => chest.rarity === key
                );
                return (
                  <MyCardsRaritySwitcherBox
                    key={key}
                    to={generateSportContextPath(
                      ANY_SPORT_MY_CARDS_ESSENCE_TYPE,
                      {
                        params: {
                          type: key,
                        },
                      }
                    )}
                    className={classNames({
                      active: type === key,
                    })}
                  >
                    <img
                      src={shardsIconUrl(key)}
                      alt=""
                      width={20}
                      height={32}
                    />

                    <LabelM bold>{shardsChest?.cardShardsCount || 0}</LabelM>
                  </MyCardsRaritySwitcherBox>
                );
              })}
          </RarityWrapper>
        </Vertical>
      </BottomSheet>
    </>
  );
};

export const UNOPENED_REWARD_BOXES = gql`
  fragment UnopenedRewardBoxes_CurrentUser on CurrentUser {
    slug
    betaTesterFootballCommonCardsGame
    unopenedRewardBoxCommon: unopenedProbabilisticBundles(
      sport: $sport
      first: 0
      rarity: common
      design: PRO
    ) {
      totalCount
    }
    unopenedRewardBoxLimited: unopenedProbabilisticBundles(
      sport: $sport
      first: 0
      rarity: limited
      design: PRO
    ) {
      totalCount
    }
    unopenedRewardBoxLimitedNeonChristmas: unopenedProbabilisticBundles(
      sport: $sport
      first: 0
      rarity: limited
      design: NEON_CHRISTMAS
    ) {
      totalCount
    }
    unopenedRewardBoxRare: unopenedProbabilisticBundles(
      sport: $sport
      first: 0
      rarity: rare
      design: PRO
    ) {
      totalCount
    }
    unopenedRewardBoxSuperRare: unopenedProbabilisticBundles(
      sport: $sport
      first: 0
      rarity: super_rare
      design: PRO
    ) {
      totalCount
    }
    unopenedRewardBoxUnique: unopenedProbabilisticBundles(
      sport: $sport
      first: 0
      rarity: unique
      design: PRO
    ) {
      totalCount
    }
    unopenedRewardBoxRivals: unopenedProbabilisticBundles(
      sport: $sport
      first: 0
      design: RIVALS
    ) {
      totalCount
    }
  }
` as TypedDocumentNode<UnopenedRewardBoxes_CurrentUser>;

const MY_CARDS_LAYOUT_QUERY = gql`
  query MyCardsLayoutQuery($sport: Sport!) {
    so5 {
      cardXpGradeThreshold(sport: $sport) {
        ...LastFixtureStory_cardXpGradeThreshold
      }
      myAvailableFixtureStory(sport: $sport) {
        id
        ...LastFixtureStory_story
      }
    }
    currentUser {
      slug
      unclaimedSo5Rewards(sport: $sport) {
        slug
        ...NewRewardsBanner_so5Reward
      }
      cardShardsChests(sport: $sport) {
        id
        rarity
        cardShardsCount
        newThresholdUnlocked
      }
      cardCollections(startedOnly: true, sport: $sport) {
        raritiesCount {
          rarity
          count
        }
      }
      ...UnopenedRewardBoxes_CurrentUser
    }
  }
  ${NewRewardsBanner.fragments.so5Reward}
  ${UNOPENED_REWARD_BOXES}
  ${LastFixtureStory.fragments.story}
  ${LastFixtureStory.fragments.cardXpGradeThreshold}
` as TypedDocumentNode<MyCardsLayoutQuery, MyCardsLayoutQueryVariables>;

const isNullableOrRarityStr = (
  type: Nullable<string>
): type is Nullable<Exclude<Rarity, Rarity.custom_series>> =>
  !type || (type in Rarity && type !== Rarity.custom_series);

export const MyCardsLayout = <
  Params extends {
    type?: string;
  },
>() => {
  const { type } = useTypedParams<Params>();
  const { generateSportContextPath } = useSportContext();
  const data = useQueryLoaderResult<MyCardsLayoutQuery>();

  if (!isNullableOrRarityStr(type) && type !== 'rivals') {
    return (
      <Navigate replace to={generateSportContextPath(ANY_SPORT_MY_CARDS)} />
    );
  }
  return (
    <RequireAuth>
      <LastFixtureStory
        story={data?.so5?.myAvailableFixtureStory}
        gradeThresholds={data?.so5?.cardXpGradeThreshold}
      />
      <MyCardsLayoutComponent currentUser={data.currentUser} type={type} />
    </RequireAuth>
  );
};

export const loader = queryLoader(MY_CARDS_LAYOUT_QUERY, ({ sport }) => ({
  sport,
}));
